import React, { useState, useEffect, useCallback, useRef } from "react";
import { Settings, defaultSettings } from "@/types/settings";
import { SettingsContext } from "@/hooks/useSettingsContext";
import { 
  fetchSettingsFromDB, 
  loadSettingsFromCache, 
  saveSettingsToCache, 
  broadcastSettingsChange 
} from "@/utils/settingsUtils";
import { enableRealtimeForTables } from "@/lib/realtime";

// Provider for the settings context
export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State for settings, loading state, and error
  const [settings, setSettings] = useState<Settings | null>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const lastRefreshTimestamp = useRef<number>(0);

  // Function to fetch settings from the database
  const fetchSettings = useCallback(async (): Promise<Settings | null> => {
    setLoading(true);
    try {
      const fetchedSettings = await fetchSettingsFromDB();
      
      if (fetchedSettings) {
        setSettings(fetchedSettings);
        saveSettingsToCache(fetchedSettings);
        lastRefreshTimestamp.current = Date.now();
        return fetchedSettings;
      } else {
        setSettings(defaultSettings);
        saveSettingsToCache(defaultSettings);
        return defaultSettings;
      }
    } catch (err: any) {
      setError(err);
      
      // Try to load from cache if DB fetch fails
      const cachedSettings = loadSettingsFromCache();
      setSettings(cachedSettings);
      return cachedSettings;
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to refresh settings with rate limiting
  const refreshSettings = useCallback(async (): Promise<Settings | null> => {
    // Rate limit to once per second
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimestamp.current;
    
    if (timeSinceLastRefresh < 1000) {
      console.log(`⚙️ SettingsProvider: Rafraîchissement ignoré (trop fréquent: ${timeSinceLastRefresh}ms)`);
      return settings;
    }
    
    console.log("⚙️ SettingsProvider: Rafraîchissement des paramètres");
    const refreshedSettings = await fetchSettings();
    
    // Broadcast an event to notify components after the update
    if (refreshedSettings) {
      broadcastSettingsChange('refresh', refreshedSettings);
    }
    
    return refreshedSettings;
  }, [fetchSettings, settings]);

  // Initial settings load
  useEffect(() => {
    console.log("⚙️ SettingsProvider: Chargement initial des paramètres");
    
    // Quick load from cache first for better UX
    const cachedSettings = loadSettingsFromCache();
    setSettings(cachedSettings);
    
    // Then get fresh data from DB
    fetchSettings();
  }, [fetchSettings]);

  // Set up event listeners and realtime
  useEffect(() => {
    console.log("⚙️ SettingsProvider: Configuration des écouteurs d'évènements et realtime");
    console.log("⚙️ SettingsProvider: Settings actuels:", settings);
    
    // Initialiser le realtime pour tous les visiteurs
    enableRealtimeForTables().then(() => {
      console.log("⚙️ SettingsProvider: Realtime activé pour tous les visiteurs");
    }).catch((error) => {
      console.error("⚠️ SettingsProvider: Erreur lors de l'activation du realtime:", error);
    });
    
    const handleSettingsChanged = (event: Event) => {
      const customEvent = event as CustomEvent;
      const source = customEvent.detail?.source || 'unknown';
      
      console.log(`⚙️ SettingsProvider: Évènement settings-changed reçu (source: ${source})`);
      
      // If update comes from mutation or realtime, we already have data
      if ((source === 'mutation' || source === 'realtime') && customEvent.detail?.data) {
        console.log(`⚙️ SettingsProvider: Mise à jour directe depuis ${source} avec:`, customEvent.detail.data);
        setSettings(customEvent.detail.data);
        saveSettingsToCache(customEvent.detail.data);
        lastRefreshTimestamp.current = Date.now();
      } else {
        // Otherwise, refresh from API
        console.log("⚙️ SettingsProvider: Rafraîchissement depuis l'API");
        refreshSettings();
      }
    };

    const handleForceRefresh = () => {
      console.log("⚙️ SettingsProvider: Évènement force-settings-refresh reçu");
      refreshSettings();
    };

    window.addEventListener('settings-changed', handleSettingsChanged);
    window.addEventListener('force-settings-refresh', handleForceRefresh);
    
    return () => {
      window.removeEventListener('settings-changed', handleSettingsChanged);
      window.removeEventListener('force-settings-refresh', handleForceRefresh);
    };
  }, [refreshSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
